import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Clock,
  DollarSign,
  Image as ImageIcon,
  Loader,
  ArrowLeft,
  Store,
  Tag,
  CheckCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import useMarketplaceStore from '../../store/marketplaceStore';
import ServiceModal from '../../components/community/ServiceModal';
import Swal from 'sweetalert2';

const ServiceManagementPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedBusiness, getBusinessDetails } = useMarketplaceStore();

  useEffect(() => {
    loadData();
  }, [businessId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await getBusinessDetails(businessId);
      await loadServices();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load services');
    } finally {
      setIsLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const response = await marketplaceAPI.getServices(businessId);
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const handleDeleteService = async (serviceId) => {
    const result = await Swal.fire({
      title: 'Delete Service',
      text: 'Are you sure you want to delete this service? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#E8ECF5',
      customClass: {
        popup: 'rounded-xl shadow-neomorph',
        confirmButton: 'px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700',
        cancelButton: 'px-4 py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover'
      }
    });
    
    if (!result.isConfirmed) return;
    
    try {
      await marketplaceAPI.deleteService(serviceId);
      toast.success('Service deleted successfully');
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete service');
    }
  };

  const filteredServices = services.filter(s => 
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const stats = {
    total: services.length,
    featured: services.filter(s => s.featured).length,
    totalOrders: services.reduce((sum, s) => sum + (s.completedOrders || 0), 0)
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] pb-16 sm:pb-0">
      {/* Header */}
      <div className="bg-white border-b border-primary-100/30 shadow-neomorph sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="mr-2 sm:mr-3 md:mr-4 p-1.5 sm:p-2 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
              </button>
              <div className="flex items-center min-w-0">
                <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-md shadow-purple-500/30 mr-2 sm:mr-4 flex-shrink-0">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-0.5 sm:mb-1">
                    <h1 className="text-base sm:text-xl md:text-2xl font-bold text-gray-800 truncate">Manage Services</h1>
                    <span className="px-1.5 sm:px-2.5 py-0.5 sm:py-1 bg-purple-100 text-purple-700 text-[9px] sm:text-xs font-medium rounded-lg border border-purple-200 whitespace-nowrap">
                      {services.length} total
                    </span>
                  </div>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 flex items-center truncate">
                    <Store className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1 flex-shrink-0" />
                    <span className="truncate">{selectedBusiness?.name}</span>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Add Service Button - Mobile optimized */}
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs sm:text-sm font-medium flex items-center justify-center"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Add Service</span>
              <span className="xs:hidden">Add</span>
            </button>
          </div>

          {/* Quick Stats - Mobile friendly */}
          <div className="flex flex-wrap gap-2 mt-3 sm:mt-4 pt-2 border-t border-primary-100/50">
            <div className="bg-white rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-neomorph-inset">
              <p className="text-[8px] sm:text-xs text-gray-500">Services</p>
              <p className="text-[10px] sm:text-sm font-semibold text-gray-800">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-neomorph-inset">
              <p className="text-[8px] sm:text-xs text-gray-500">Featured</p>
              <p className="text-[10px] sm:text-sm font-semibold text-yellow-600">{stats.featured}</p>
            </div>
            <div className="bg-white rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-neomorph-inset">
              <p className="text-[8px] sm:text-xs text-gray-500">Total Orders</p>
              <p className="text-[10px] sm:text-sm font-semibold text-green-600">{stats.totalOrders}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30 mb-4 sm:mb-6">
          <div className="relative">
            <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services..."
              className="w-full pl-8 sm:pl-10 pr-2.5 sm:pr-4 py-1.5 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs sm:text-sm"
            />
          </div>
        </div>

        {/* Services Grid - 2 columns on mobile */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30 animate-pulse">
                <div className="h-24 sm:h-40 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg mb-2 sm:mb-4"></div>
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4 mb-1.5 sm:mb-2"></div>
                <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2 mb-2 sm:mb-4"></div>
                <div className="h-6 sm:h-8 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        ) : filteredServices.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-6">
            {filteredServices.map(service => (
              <div key={service.id} className="bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30 hover:shadow-neomorph-hover transition-all">
                {/* Service Image */}
                {service.images?.[0] ? (
                  <img
                    src={service.images[0]}
                    alt={service.title}
                    className="w-full h-24 sm:h-48 object-cover rounded-lg mb-2 sm:mb-4"
                  />
                ) : (
                  <div className="w-full h-24 sm:h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-2 sm:mb-4 flex items-center justify-center">
                    <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                  </div>
                )}
                
                <div className="space-y-1.5 sm:space-y-3">
                  {/* Title and Featured Badge */}
                  <div className="flex flex-wrap items-start justify-between gap-1">
                    <h3 className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-2 flex-1">{service.title}</h3>
                    {service.featured && (
                      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-[8px] sm:text-xs font-bold rounded-lg whitespace-nowrap">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  {/* Description - Hidden on mobile */}
                  <p className="hidden sm:block text-sm text-gray-600 line-clamp-2">
                    {service.description}
                  </p>
                  
                  {/* Price and Delivery Time */}
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <div>
                      <span className="text-sm sm:text-lg font-bold text-gray-800">
                        ₦{service.basePrice?.toLocaleString()}
                      </span>
                      {service.priceRange?.max && service.priceRange.max > service.basePrice && (
                        <span className="text-[9px] sm:text-sm text-gray-500 ml-0.5 sm:ml-1">
                          - ₦{service.priceRange.max.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {service.deliveryTime && (
                      <div className="flex items-center text-[9px] sm:text-sm text-gray-600 bg-gray-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                        <Clock className="h-2 w-2 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        {service.deliveryTime}
                      </div>
                    )}
                  </div>
                  
                  {/* Stats Row - Mobile optimized */}
                  <div className="flex flex-wrap items-center justify-between gap-1 text-[9px] sm:text-sm">
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                      {service.completedOrders || 0} orders
                    </div>
                    {service.category && (
                      <span className="flex items-center text-gray-500">
                        <Tag className="h-2 w-2 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        <span className="truncate max-w-[60px] sm:max-w-none">{service.category}</span>
                      </span>
                    )}
                  </div>
                  
                  {/* Actions and ID */}
                  <div className="flex items-center justify-between pt-1.5 sm:pt-3 border-t border-primary-100/50">
                    <div className="flex space-x-1 sm:space-x-2">
                      <button
                        onClick={() => {/* View service details */}}
                        className="p-1.5 sm:p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
                        title="View"
                      >
                        <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedService(service);
                          setShowAddModal(true);
                        }}
                        className="p-1.5 sm:p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all"
                        title="Edit"
                      >
                        <Edit className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteService(service.id)}
                        className="p-1.5 sm:p-2 bg-red-50 rounded-lg hover:bg-red-100 transition-all"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
                      </button>
                    </div>
                    
                    <span className="text-[7px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 text-gray-600 rounded-lg truncate max-w-[60px] sm:max-w-none">
                      ID: {service.id.slice(0, 6)}...
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-neomorph p-6 sm:p-8 text-center border border-primary-100/30">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-purple-50 shadow-neomorph-inset mb-3 sm:mb-4">
              <TrendingUp className="h-7 w-7 sm:h-10 sm:w-10 text-purple-400" />
            </div>
            <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">No services found</h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
              {searchQuery ? 'Try a different search' : 'Start adding services to your business'}
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 sm:px-6 py-1.5 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs sm:text-sm inline-flex items-center"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Add First Service
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Service Modal */}
      <ServiceModal
        isOpen={showAddModal}
        businessId={businessId}
        service={selectedService}
        onClose={() => {
          setShowAddModal(false);
          setSelectedService(null);
        }}
        onSuccess={() => {
          loadServices();
          setShowAddModal(false);
          setSelectedService(null);
        }}
      />
    </div>
  );
};

export default ServiceManagementPage;